import React from "react";
import { useSelector } from "react-redux";
import Modal from "../../../../../../../components/Modal/Modal";
import Button from "../../../../../../../components/Button/Button";
import DeleteTrashOpenIcon from "../../../../../../../assets/svg/delete-trash-open-icon";
import { HelpDeskTicket } from "../utils/types";
import { TicketCard } from "./TicketCard";
import type { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

interface DeleteTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmDelete: () => Promise<void>;
  ticket: HelpDeskTicket;
  isDeleting: boolean;
}

const DeleteTicketModal: React.FC<DeleteTicketModalProps> = ({
  open,
  onOpenChange,
  onConfirmDelete,
  ticket,
  isDeleting,
}) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      preventOutsideClick={false}
      customHeader={
        <div className="flex items-center gap-2 flex-1">
          <DeleteTrashOpenIcon className="w-4 h-4 text-verba-red-alert dark:text-verba-red-alert-dk" />
          <span className="text-heading-text dark:text-heading-text-dk font-medium text-base mt-0.5">
            {helpdeskLabels.deleteTicket}
          </span>
        </div>
      }
      headerClassName="px-4 pt-4 pb-0"
      contentClassName="w-[326px] border border-border-gray dark:border-border-gray-dk bg-fixed-white dark:bg-popper-bg-dk backdrop-blur-[10px]"
      overlayClassName="bg-black/80"
      bodyClassName="!p-0"
    >
      <div className="flex flex-col p-4" onClick={(e) => e?.stopPropagation()}>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-normal text-data dark:text-data-dk leading-relaxed mb-3">
            Deleting this ticket will permanently remove all associated logs,
            attachments and related content. This action cannot be undone.
          </p>
          <p className="text-xs font-normal text-data dark:text-data-dk leading-[14px]">
            Are you sure you want to continue?
          </p>
        </div>

        <div className="pointer-events-none mt-[24px] mb-[48px]">
          <TicketCard ticket={ticket} stageKey={ticket?.stage_key ?? ""} />
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            innerText="Cancel"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            buttonContainerClass="min-w-[100px] min-h-[26px] py-[2px] px-2 text-md border border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk rounded-md cursor-pointer"
            innerTextClass="text-pure-white dark:text-pure-white-dk text-md font-semibold"
          />
          <Button
            innerText={helpdeskLabels.deleteTicket}
            onClick={onConfirmDelete}
            disabled={isDeleting}
            isLoading={isDeleting}
            buttonContainerClass="disabled:opacity-30 min-w-[100px] min-h-[26px] py-[2px] px-6 text-md border border-verba-red-alert dark:border-verba-red-alert-dk bg-transparent dark:bg-transparent rounded-md cursor-pointer"
            innerTextClass="text-verba-red-alert dark:text-verba-red-alert-dk text-md font-semibold"
            loaderColor="text-verba-red-alert dark:text-verba-red-alert-dk"
            loaderSize={15}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTicketModal;
